'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { useScopeCalculations } from '../../calculatedAttributes/store';
import { ApplyCtaLink } from '../../components/ApplyCta';
import { resolveTiCtaLabel } from '../../components/ApplyCta/resolveTiCtaLabel';
import Button from '../../components/Button';
import { usePinnedBottomHeight } from '../../hooks/usePinnedBottomHeight';
import { useStickyFooterApplyClick } from '../../hooks/useStickyFooterApplyClick';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { configureTiSearchResultParameters } from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getFulfilmentPath } from '../../utils/tiAttributes';
import {
  resolveTiFooterCopy,
  resolveTiSupportingWording,
} from '../../utils/tiFulfilment';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';

export type StickyFooterCtaProps = {
  // The active tier's product. When absent (no tier selected yet) the footer
  // renders nothing (CMS-1643 AC-8).
  product?: CmsProduct;
  // Active tier display name, for the "Apply for :product-name" copy.
  tierName?: string;
  // Provider name, for the :provider-name anchor in click_out supporting wording.
  providerName?: string;
  // dataLayer sink for the Apply-Now click event (CMS-1861 Event 3). Supplied by
  // the fe wrapper; when absent the CTA still navigates, just untracked.
  pushDataLayer?: PushDataLayerFn;
};

/**
 * TI provider page sticky footer CTA (CMS-1643). A `position: fixed` bottom bar
 * for the active tier: "Apply for :product-name" copy, a standardised CTA
 * (`ApplyCtaLink` + `resolveTiCtaLabel`), and fulfilment-path supporting wording
 * (omitted when the path is absent). Pinned for the whole page once a tier is
 * selected (CMS-1643: "for the full page lifecycle"); it steps aside only while
 * the site footer is on screen. The page bottom spacer that stops it covering
 * the last section is added by the fe wrapper (Task 10d). Renders nothing until
 * a tier is selected.
 */
export const StickyFooterCta: React.FC<StickyFooterCtaProps> = ({
  product,
  tierName,
  providerName,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();
  // The trip priced for this scope. Since the calculated-attributes refactor it
  // comes from the calculation data, not the page context.
  const { travelQuote: travelQuotesData } = useScopeCalculations();
  // search_result_parameter carries the travel-quote search criteria (CMS-1861
  // Event 3). Present only on a TI page reached via a quote; the spec says to
  // omit it rather than send an empty string, so guard on a resolved value.
  const searchResultParameter =
    configureTiSearchResultParameters(travelQuotesData);
  const sendAnalyticsForApply = useStickyFooterApplyClick({
    product,
    pushDataLayer,
    extraData: searchResultParameter
      ? { search_result_parameter: searchResultParameter }
      : undefined,
  });
  // Apply-CTA labels and the footer heading are shared page-wide
  // (BaseTranslations); the fulfilment-path supporting wording is block-local
  // (./i18n) since only this block uses it. Merge so the resolvers see both.
  const staticText = {
    ...getClientTranslations(BaseTranslations, context),
    ...getClientTranslations(TRANSLATIONS, context),
  };
  const [overSiteFooter, setOverSiteFooter] = useState(false);
  const shown = !overSiteFooter;
  // What this bar covers, for the Sort & Filter button and rail to stay clear of.
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  usePinnedBottomHeight(bar, shown);

  // Step aside while the site footer is on screen, so the bar never covers it.
  // The site footer is the document's last <footer>; content may hold others.
  useEffect(() => {
    const footers = document.querySelectorAll('footer');
    const node = footers[footers.length - 1];
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setOverSiteFooter(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!product) return null;

  const fulfilmentPath = getFulfilmentPath(product);
  const ctaLabel = resolveTiCtaLabel({
    brand: context.brand,
    staticText,
    fulfilmentPath,
    callToAction: product.call_to_action,
  });
  const footerCopy = resolveTiFooterCopy({ staticText, tierName });
  const supportingWording = resolveTiSupportingWording({
    staticText,
    fulfilmentPath,
    providerName,
  });

  return (
    // When hidden the footer sits off-screen via `translate-y-full` but stays in
    // the DOM; `inert` keeps its Apply CTA out of the tab order and a11y tree
    // until it slides in.
    <div
      ref={setBar}
      data-component="ti-sticky-footer"
      data-fulfilment-path={fulfilmentPath ?? 'none'}
      inert={!shown}
      className={classNames(
        'fixed bottom-0 left-0 w-full z-[60] duration-300 ease-out',
        shown ? 'translate-y-0' : 'translate-y-full',
      )}
    >
      <div className="bg-white shadow px-spacing/padding/x py-spacing/padding/y">
        <div className="w-full md:max-w-[990px] mx-auto flex flex-col gap-spacing/gap/x-xs md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-spacing/gap/x-xs">
            <div className="mhg-alpha-typography-title-1 text-fill/primary-dark">
              {footerCopy}
            </div>
            {supportingWording && (
              <div className="mhg-alpha-typography-body-2 text-text/secondary">
                {supportingWording}
              </div>
            )}
          </div>
          <ApplyCtaLink
            product={product}
            target="_blank"
            rel="noreferrer noopener"
            className="block w-full md:inline md:w-auto"
          >
            <Button
              className="mhg-alpha-btn-small w-full md:w-auto"
              styleType="cta"
              onClick={sendAnalyticsForApply}
            >
              {ctaLabel}
            </Button>
          </ApplyCtaLink>
        </div>
      </div>
    </div>
  );
};
